import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface ChatVisibilityOptions {
  persistPreference?: boolean;
}

export type ModalVariant =
  | "invite"
  | "settings-user"
  | "info"
  | "new_board"
  | "table_settings"
  | "audio_permissions"
  | "kicked_for_inactivity"
  | "inactivity_warning"
  | "outdated_office"
  | "sidebar_app_warning"
  | "survey";

export interface LayoutStore {
  chatVisible: boolean;
  chatOpenByDefault: boolean;
  applyChatDefaultPreference: () => void;
  setChatOpenByDefault: (visible: boolean) => void;
  setChatVisible: (visible: boolean, options?: ChatVisibilityOptions) => void;

  useSimplifiedOfficePreference: boolean | null;
  setUseSimplifiedOffice: (enabled: boolean) => void;

  personRosterVisible: boolean;
  showPersonRoster: () => void;
  hidePersonRoster: () => void;
  setPersonRosterVisible: (visible: boolean) => void;
  togglePersonRoster: () => void;

  modalVariant: ModalVariant | null;
  modalProps: object;
  setModalVariant: (variant: ModalVariant, props?: object) => void;
  isLeavingOffice: boolean;
  setIsLeavingOffice: (isLeavingOffice: boolean) => void;
  hideModal: () => void;
  hideSurveyModal: () => void;

  avatarMenu: {
    context: unknown;
  } | null;
  openAvatarMenu: (context: unknown) => void;
  closeAvatarMenu: () => void;
}

interface PersistedLayoutStore {
  chatOpenByDefault?: boolean;
  useSimplifiedOfficePreference?: boolean | null;
}

export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set) => ({
      applyChatDefaultPreference: () =>
        set((state) => ({ chatVisible: state.chatOpenByDefault })),
      avatarMenu: null,
      chatOpenByDefault: true,
      chatVisible: true,
      closeAvatarMenu: () => set({ avatarMenu: null }),
      hideModal: () =>
        set((state) =>
          state.modalVariant === "survey" ? state : { modalVariant: null }
        ),
      hidePersonRoster: () =>
        set({
          personRosterVisible: false,
        }),
      hideSurveyModal: () =>
        set((state) =>
          state.modalVariant === "survey" ? { modalVariant: null } : state
        ),
      isLeavingOffice: false,
      modalProps: {},
      modalVariant: null,
      openAvatarMenu: (context: unknown) => set({ avatarMenu: { context } }),
      personRosterVisible: false,
      setChatOpenByDefault: (visible: boolean) =>
        set({ chatOpenByDefault: visible }),
      setChatVisible: (visible: boolean, options: ChatVisibilityOptions = {}) =>
        set((state) => ({
          chatOpenByDefault: options.persistPreference
            ? visible
            : state.chatOpenByDefault,
          chatVisible: visible,
          personRosterVisible: visible ? false : state.personRosterVisible,
        })),
      setIsLeavingOffice: (isLeavingOffice: boolean) =>
        set({ isLeavingOffice }),
      setModalVariant: (variant: ModalVariant, props: object = {}) => {
        set((state) => {
          if (variant === "survey" && state.modalVariant !== null) {
            return state;
          }

          return { modalProps: props, modalVariant: variant };
        });
      },
      setPersonRosterVisible: (visible: boolean) =>
        set((state) => ({
          chatVisible: visible ? false : state.chatVisible,
          personRosterVisible: visible,
        })),
      setUseSimplifiedOffice: (enabled: boolean) =>
        set({ useSimplifiedOfficePreference: enabled }),
      showPersonRoster: () =>
        set({
          chatVisible: false,
          personRosterVisible: true,
        }),
      togglePersonRoster: () =>
        set((state) => {
          const nextVisible = !state.personRosterVisible;
          return {
            chatVisible: nextVisible ? false : state.chatVisible,
            personRosterVisible: nextVisible,
          };
        }),
      useSimplifiedOfficePreference: null,
    }),
    {
      merge: (persistedState, currentState) => {
        const persisted = persistedState as PersistedLayoutStore | undefined;
        const chatOpenByDefault =
          typeof persisted?.chatOpenByDefault === "boolean"
            ? persisted.chatOpenByDefault
            : currentState.chatOpenByDefault;
        const useSimplifiedOfficePreference =
          typeof persisted?.useSimplifiedOfficePreference === "boolean" ||
          persisted?.useSimplifiedOfficePreference === null
            ? persisted.useSimplifiedOfficePreference
            : currentState.useSimplifiedOfficePreference;

        return {
          ...currentState,
          chatOpenByDefault,
          chatVisible: chatOpenByDefault,
          useSimplifiedOfficePreference,
        };
      },
      name: "layout-store",
      partialize: (state) => ({
        chatOpenByDefault: state.chatOpenByDefault,
        useSimplifiedOfficePreference: state.useSimplifiedOfficePreference,
      }),
      storage: createJSONStorage(() => localStorage),
    }
  )
);
